import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Button,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import InfoIcon from '@mui/icons-material/Info';
import COLORS from '../../../../resources/style/Themes';

const CreateAppVersionModal = ({ show, onHide, onCreate, objectType }) => {
  const [version, setVersion] = useState('');
  const [rolloutTimestamp, setRolloutTimestamp] = useState(null);
  const [required, setRequired] = useState(false);
  const [validation, setValidation] = useState({});
  const [notificationDays, setNotificationDays] = useState([]);
  const [isAddingDay, setIsAddingDay] = useState(false);
  const [newDay, setNewDay] = useState('');

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    const epochRolloutTimestamp = rolloutTimestamp
      ? rolloutTimestamp.valueOf()
      : null;

    const newAppVersion = {
      notification_days: notificationDays,
      required: required,
      rollout_timestamp: epochRolloutTimestamp,
      version: parseFloat(version),
      object_type: objectType,
    };

    onCreate(newAppVersion);
    resetValues();
    onHide();
  };

  const validateFields = () => {
    let isValid = true;
    const newValidation = {};

    if (!version || version.length === 0) {
      newValidation.version = 'error';
      isValid = false;
    }

    if (!notificationDays || notificationDays.length === 0) {
      newValidation.notificationDays = 'error';
      isValid = false;
    }

    if (!rolloutTimestamp || !rolloutTimestamp.isValid()) {
      newValidation.rolloutTimestamp = 'error';
      isValid = false;
    }

    setValidation(newValidation);
    return isValid;
  };

  const handleAddDay = () => {
    setIsAddingDay(true);
  };

  const handleSaveDay = () => {
    const dayInt = parseInt(newDay.trim(), 10);
    if (!isNaN(dayInt) && !notificationDays.includes(dayInt)) {
      setNotificationDays([...notificationDays, dayInt]);
      setNewDay('');
    } else {
      console.error('Invalid day input or day already exists:', newDay);
    }
    setIsAddingDay(false);
  };

  const handleDeleteDay = (idx) => {
    const updatedDays = notificationDays.filter((_, index) => index !== idx);
    setNotificationDays(updatedDays);
  };

  const resetValues = () => {
    setValidation({});
    setVersion('');
    setRolloutTimestamp(null);
    setNotificationDays([]);
    setRequired(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={show}
        onClose={() => {
          onHide();
          resetValues();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
        >
          {`Add ${objectType === 'version_android' ? 'Android App Version' : objectType === 'version_ios' ? 'iOS App Version' : 'Web App Version'}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Version Number"
            fullWidth
            error={validation.version === 'error'}
            helperText={
              validation.version === 'error'
                ? 'Please enter a valid version number (e.g., 1.2).'
                : ''
            }
            value={version}
            onChange={(e) => {
              const value = e.target.value;
              const regex = /^\d*\.?\d*$/;
              if (regex.test(value)) {
                setVersion(value);
              }
            }}
            margin="normal"
          />

          {/* Notification Days Label */}
          <div
            style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Notification Days (Required)
            </Typography>
            <Tooltip
              title="For each number added, an in-app notification will be displayed that number of days before the rollout timestamp date."
              placement="right"
            >
              <InfoIcon
                sx={{
                  fontSize: 16,
                  ml: 1,
                  color: COLORS.SAFEGUARD_BLUE,
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </div>
          <>
            <div style={{ marginBottom: '10px' }}>
              {notificationDays.map((num, idx) => (
                <Chip
                  key={idx}
                  label={num.toString()}
                  onDelete={() => handleDeleteDay(idx)}
                  style={{ marginRight: '5px', marginTop: '2px' }}
                />
              ))}
              {isAddingDay ? (
                <TextField
                  size="small"
                  value={newDay}
                  onChange={(e) => setNewDay(e.target.value)}
                  onBlur={handleSaveDay}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveDay();
                    }
                  }}
                  autoFocus
                  style={{ verticalAlign: 'bottom' }}
                />
              ) : (
                <Chip
                  label="+ Add"
                  onClick={handleAddDay}
                  style={{ marginLeft: '5px', cursor: 'pointer' }}
                />
              )}
              {validation.notificationDays === 'error' && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                  }}
                >
                  At least one notification day is required.
                </div>
              )}
            </div>
          </>

          {/* Rollout Timestamp Label */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 500, marginTop: '16px' }}
          >
            Rollout Timestamp (Required)
          </Typography>
          <>
            <DateTimePicker
              label="Rollout Date (PST)"
              value={rolloutTimestamp}
              onChange={(newValue) => setRolloutTimestamp(newValue)}
              minDateTime={dayjs()}
              maxDateTime={dayjs().add(10, 'year')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: validation.rolloutTimestamp === 'error',
                  helperText:
                    validation.rolloutTimestamp === 'error'
                      ? 'Please enter a date for rollout.'
                      : '',
                },
              }}
            />
          </>

          <TextField
            select
            label="Required"
            fullWidth
            value={String(required)}
            onChange={(e) => setRequired(e.target.value === 'true')}
            SelectProps={{
              native: true,
            }}
            margin="normal"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="secondary" variant="contained">
            Add Version
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateAppVersionModal;
